import { ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import qs from 'query-string';
import { FETCHING_CREDENTIALS_FAILED } from '../../utilities/error';
import { CredentialsRequest } from '../../lib-api/services/Credentials';
import { RefreshableItem } from '../../models/refreshableItem';
import { ApplicationError } from '../../components/ApplicationErrorBoundary/errors/ApplicationError';
import {
  AccountSelectionIdentifierTypes,
  Credentials,
  CredentialsEntity,
  ResetCredentialsResponse,
} from '../../models/credentials';
import { CommonError } from '../../stores/app-state-errors';
import { currencyCodeToSymbol } from '../../utilities/utils';
import { ListAccount } from '../../banking/components/AccountList/AccountListItem';
import { containsCredentials } from './credentials';
import {
  AuthenticateCredentialsRequest,
  credentialsClient,
  CredentialsClient,
  RefreshCredentialsRequest,
  SelectedAccount,
} from './credentialsClient';

export type SubmitCredentialsPayload = {
  userCredentials: CredentialsEntity | undefined;
  existingCredentials: CredentialsEntity | undefined;
  refreshableItems: RefreshableItem[];
  authenticate: boolean;
  credentialsRequest: CredentialsRequest;
};

export type CredentialsService = {
  createCredentials: (
    request: CredentialsRequest,
    refreshableItems: RefreshableItem[]
  ) => Promise<CredentialsEntity>;
  getCredentials: (id: string) => Promise<CredentialsEntity>;
  updateCredentials: (id: string, request: CredentialsRequest) => Promise<CredentialsEntity>;
  getQRCredentials: (id: string) => Promise<string>;
  supplement: (id: string, information: Record<string, unknown> | null) => Promise<void>;
  refreshCredentials: (request: RefreshCredentialsRequest) => Promise<null>;
  authenticateCredentials: ({ id, request }: AuthenticateCredentialsRequest) => Promise<null>;
  resetCredentialsStatus: (id: string) => Promise<ResetCredentialsResponse>;
  getCredentialsResetStatus: (id: string) => Promise<ResetCredentialsResponse>;
  supplementalInfoApiCallback: (supplementalInfoState: string) => Promise<void>;
  deleteCredentials: (credentialsId: string) => Promise<void>;
  submitCredentials: (payload: SubmitCredentialsPayload) => Promise<CredentialsEntity>;
  getAccounts: (id: string) => Promise<ListAccount[]>;
  postAccountSelection: (id: string, selectedAccounts: string[]) => Promise<void>;
};

export const CREATE_CREDENTIALS_FAILED: CommonError = {
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.REQUEST_FAILED_CREATE_CREDENTIALS,
};

export const UPDATE_CREDENTIALS_FAILED: CommonError = {
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.REQUEST_FAILED_REFRESH_CREDENTIALS,
};

export const AUTH_CREDENTIALS_FAILED: CommonError = {
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.REQUEST_FAILED_AUTHENTICATE_CREDENTIALS_FAILED,
};

export const FETCH_CREDENTIALS_RESET_STATUS_FAILED: CommonError = {
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.REQUEST_FAILED_FETCH_CREDENTIALS_RESET_STATUS_FAILED,
};

export const FETCH_QR_CREDENTIALS_FAILED: CommonError = {
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.REQUEST_FAILED_FETCH_QR_CREDENTIALS_FAILED,
};

export const REFRESH_CREDENTIALS_FAILED: CommonError = {
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.REQUEST_FAILED_REFRESH_CREDENTIALS_FAILED,
};

export const RESET_CREDENTIALS_STATUS_FAILED: CommonError = {
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.REQUEST_FAILED_RESET_CREDENTIALS_STATUS_FAILED,
};

export const FETCH_SUPPLEMENTAL_INFO_FAILED: CommonError = {
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.REQUEST_FAILED_FETCH_SUPPLEMENTAL_INFO_FAILED,
};

export const SUPPLEMENT_CREDENTIALS_FAILED: CommonError = {
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.REQUEST_FAILED_SUPPLEMENT_CREDENTIALS,
};

export const DELETE_CREDENTIALS_FAILED: CommonError = {
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.REQUEST_FAILED_DELETE_CREDENTIALS_FAILED,
};

export const GET_CREDENTIALS_ACCOUNTS_FAILED: CommonError = {
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.REQUEST_FAILED_GET_CREDENTIALS_ACCOUNTS_FAILED,
};

export const POST_SELECTED_ACCOUNT_FAILED: CommonError = {
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.REQUEST_FAILED_POST_SELECTED_ACCOUNT_FAILED,
};

export const createCredentialsService = (client: CredentialsClient): CredentialsService => ({
  async createCredentials(request, refreshableItems) {
    const query = qs.stringify({ items: refreshableItems });

    const response = await client.create(request, query);

    if (!response.isOk() || !response.data) {
      throw new ApplicationError(CREATE_CREDENTIALS_FAILED);
    }

    return response.data;
  },

  async getCredentials(id) {
    const query = qs.stringify({ detailedError: true, templatesScreen: true }, { skipNull: true });
    const response = await client.get(id, query);

    if (!response.isOk() || !response.data) {
      throw new ApplicationError(FETCHING_CREDENTIALS_FAILED);
    }

    return response.data;
  },

  async updateCredentials(id, request) {
    const response = await client.update(id, request);

    if (!response.isOk() || !response.data) {
      throw new ApplicationError(UPDATE_CREDENTIALS_FAILED);
    }

    return response.data;
  },

  async authenticateCredentials(request: AuthenticateCredentialsRequest): Promise<null> {
    const response = await client.authenticate(request);

    if (!response.isOk()) {
      throw new ApplicationError(AUTH_CREDENTIALS_FAILED);
    }

    return response.data;
  },

  async getCredentialsResetStatus(id: string): Promise<ResetCredentialsResponse> {
    const response = await client.getCredentialsResetStatus(id);

    if (!response.isOk() || !response.data) {
      throw new ApplicationError(FETCH_CREDENTIALS_RESET_STATUS_FAILED);
    }

    return response.data;
  },

  async getQRCredentials(id: string): Promise<string> {
    const response = await client.getQR(id);

    if (!response.isOk() || !response.data) {
      throw new ApplicationError(FETCH_QR_CREDENTIALS_FAILED);
    }

    return response.data;
  },

  async refreshCredentials({
    id,
    refreshableItems,
    request,
    authenticate,
  }: RefreshCredentialsRequest): Promise<null> {
    const query = qs.stringify({ items: refreshableItems, authenticate: authenticate.toString() });

    const response = await client.refresh({ id, request }, query);

    if (!response.isOk()) {
      throw new ApplicationError(REFRESH_CREDENTIALS_FAILED);
    }

    return response.data;
  },

  async resetCredentialsStatus(id: string): Promise<ResetCredentialsResponse> {
    const response = await client.resetCredentialsStatus(id);

    if (!response.isOk() || !response.data) {
      throw new ApplicationError(RESET_CREDENTIALS_STATUS_FAILED);
    }

    return response.data;
  },

  async supplementalInfoApiCallback(supplementalInfoState: string) {
    await client.supplementalInfoApiCallback(supplementalInfoState);
  },

  async supplement(id: string, information: Record<string, unknown> | null): Promise<void> {
    const response = await client.supplement(id, information);

    if (!response.isOk()) {
      throw new ApplicationError(SUPPLEMENT_CREDENTIALS_FAILED);
    }

    return response.data;
  },
  async deleteCredentials(credentialsIs: string) {
    const response = await client.delete(credentialsIs);

    if (!response.isOk()) {
      throw new ApplicationError(DELETE_CREDENTIALS_FAILED);
    }
  },
  async submitCredentials({
    existingCredentials,
    userCredentials,
    refreshableItems,
    authenticate,
    credentialsRequest,
  }) {
    if (existingCredentials && Credentials.hasError(existingCredentials)) {
      // We can only update credentials which are in ERROR state.
      // If we try to update credentials in CREATED or UPDATED state we get 400 response back because `username` field is immutable.
      return this.updateCredentials(existingCredentials.id, credentialsRequest);
    }

    if (userCredentials) {
      const existingCredentialsCanBeRefreshed = containsCredentials(
        [userCredentials],
        credentialsRequest
      );

      if (existingCredentialsCanBeRefreshed) {
        await this.refreshCredentials({
          id: existingCredentialsCanBeRefreshed.id,
          authenticate,
          refreshableItems,
          request: {
            appUri: credentialsRequest.appUri,
            productNames: credentialsRequest.productNames,
          },
        });

        return this.getCredentials(userCredentials.id);
      }
    }

    return this.createCredentials(credentialsRequest, refreshableItems);
  },
  async getAccounts(credentialsId) {
    const response = await client.getAccountsSelection(credentialsId);

    if (!response.isOk()) {
      throw new ApplicationError(GET_CREDENTIALS_ACCOUNTS_FAILED);
    }

    return response.data.map<ListAccount>(({ id, accountBalance, identifiers }) => ({
      id,
      balance: `${currencyCodeToSymbol(accountBalance.currency)}${accountBalance.value}`,
      name:
        identifiers.find((el) => el.type === AccountSelectionIdentifierTypes.ACCOUNT_NAME)?.value ||
        '',
      accountNumber:
        identifiers.find((el) => el.type === AccountSelectionIdentifierTypes.ACCOUNT_NUMBER)
          ?.value || '',
      holderName: '',
      iban: '',
    }));
  },
  async postAccountSelection(id, selectedAccounts) {
    const mapped = selectedAccounts.map<SelectedAccount>((el) => ({ id: el }));
    const response = await client.postAccountSelection(id, mapped);

    if (!response.isOk()) {
      throw new ApplicationError(POST_SELECTED_ACCOUNT_FAILED);
    }

    return response.data;
  },
});

export const credentialsService = createCredentialsService(credentialsClient);
